import { useState, type PointerEvent } from 'react'

export type ViewDay = { date: string; views: number }

function formatDay(iso: string) {
  const [year, month, day] = iso.split('-').map(Number)
  if (!year || !month || !day) return iso
  return new Date(year, month - 1, day).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
}

export default function ViewsDayChart({
  days,
  heightClass = 'h-16',
  minBar = 6,
}: {
  days: ViewDay[]
  heightClass?: string
  minBar?: number
}) {
  const max = Math.max(...days.map((day) => day.views), 1)
  const [hoverIndex, setHoverIndex] = useState<number | null>(null)
  const hover = hoverIndex == null ? null : days[hoverIndex]
  const barPct = hover ? Math.max(minBar, (hover.views / max) * 100) : 0
  const leftPct = hoverIndex == null || !days.length ? 50 : ((hoverIndex + 0.5) / days.length) * 100

  function indexAt(event: PointerEvent<HTMLDivElement>) {
    if (!days.length) return null
    const rect = event.currentTarget.getBoundingClientRect()
    const ratio = rect.width <= 0 ? 0 : (event.clientX - rect.left) / rect.width
    return Math.min(days.length - 1, Math.max(0, Math.floor(ratio * days.length)))
  }

  return (
    <div className="relative mt-3 overflow-visible pt-7">
      <div
        className={`relative flex cursor-crosshair items-end gap-px ${heightClass}`}
        onPointerMove={(event) => setHoverIndex(indexAt(event))}
        onPointerLeave={() => setHoverIndex(null)}
      >
        {hover ? (
          <div
            role="status"
            className="pointer-events-none absolute z-10 -translate-x-1/2 whitespace-nowrap text-center text-[11px] tracking-[0.08em] uppercase text-ink"
            style={{ left: `${leftPct}%`, bottom: `calc(${barPct}% + 6px)` }}
          >
            {formatDay(hover.date)} · {hover.views.toLocaleString()}
          </div>
        ) : null}
        {days.map((day, index) => (
          <div key={`${day.date}-${index}`} className="flex h-full min-w-0 flex-1 items-end">
            <span
              className={`admin-grow-y block w-full transition-colors duration-150 ${hoverIndex === index ? 'bg-rose' : 'bg-rose/80'}`}
              style={{ height: `${Math.max(minBar, (day.views / max) * 100)}%`, animationDelay: `${index * 18}ms` }}
            />
          </div>
        ))}
      </div>
    </div>
  )
}
